import { useConvexAction } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { useMutation } from '@tanstack/react-query'
import { Languages } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface TranslateButtonProps {
  readonly jobId: Id<'jobs'>
  readonly sourceLanguage: string
  readonly targetLanguage: string
  readonly text: string
  readonly isHtml: boolean
  readonly disabled: boolean
  readonly onTranslated: (translatedText: string) => void
  readonly onError?: (reason: 'rate_limited' | 'not_found' | 'unknown') => void
}

const noop = (): void => undefined

export function TranslateButton({
  jobId,
  sourceLanguage,
  targetLanguage,
  text,
  isHtml,
  disabled,
  onTranslated,
  onError = noop,
}: TranslateButtonProps) {
  const { t } = useTranslation()

  const { mutateAsync: translate, isPending } = useMutation({
    mutationFn: useConvexAction(
      api.features.jobs.translate_job_field.translateMyJobField,
    ),
  })

  const handleClick = async (): Promise<void> => {
    try {
      const result = await translate({
        jobId,
        sourceLanguage,
        targetLanguage,
        text,
        isHtml,
      })
      if (result.ok) {
        onTranslated(result.translatedText)
      } else {
        onError(result.reason)
      }
    } catch {
      onError('unknown')
    }
  }

  const sourceLanguageKey = `jobs.form.language${sourceLanguage.charAt(0).toUpperCase()}${sourceLanguage.slice(1)}`
  const sourceLabel = (t as (key: string) => string)(sourceLanguageKey)

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={disabled || isPending || !text.trim()}
      onClick={() => void handleClick()}
    >
      <Languages className="h-3.5 w-3.5 mr-1.5" />
      {isPending
        ? t('jobs.translations.generating')
        : t('jobs.translations.translateFrom', { language: sourceLabel })}
    </Button>
  )
}
